import dayjs from "dayjs";
import { AdditionalContactsInput, BaseFieldFragment, CalendarEventLocationInput, Charge, Field, FindQueryVariables, FullJobFragment, FullUserFragment, Job_LocationsFragment, JobDetailsV2Fragment, Location, LocationBase, LocationCreateBase, UpdateJobCustomerProfileInput, User } from "graphql.generated";
import { cloneDeep, get, isArray, isEmpty, isNil, isPlainObject, transform } from "lodash";


import { ADDABLE_LOCATIONS_V2, EventTypeInfo, eventTypeInfoMapV2, JOB_ROLE_MAP, LOCK_EXEMPT_NON_REVENUE_EVENT_TYPES } from "../global.constants";

import { getEventInvoicedStatus, isFinalizedInvoice } from '../invoices/invoices.utils';

import { AdditionalContactState, additionalContactInitialState, JobCreateCustomerState } from './job-create/job-create-customer/job-create-customer.state';
import { ChargeWithLockedAndInvoicedFlag, DistanceLocation } from "./job-create/job-interfaces";
import { JobToolFields, JobToolState, LocationWithHowSetIndicator, Summary } from "./job-tool.reducer";

export type GetRoutesInput = {
  dock?: DistanceLocation,
  start?: DistanceLocation,
  end?: DistanceLocation,
}

/**
 * Placeholder written to `navigatorCurrentStep` when a navigator is opened but nothing has been
 * confirmed yet. It is not a step id and never matches one in a navigator script.
 */
export const NAVIGATOR_STARTED_SENTINEL = 'started';

/** One step's timing as stored in `navigatorData.steps`. */
export interface StepTiming {
  startedAt: number | null;
  completedAt: number | null;
}

/** What the crew's final "Finish" writes as the step: every step in the script is done. */
export const NAVIGATOR_FINISHED_STEP = 'completed';

/** A step whose timings are all null: the wire signal that deletes the step's entry and mirrors. */
const CLEARED_STEP: StepTiming = { startedAt: null, completedAt: null };

/**
 * The timing write that goes with ops moving a crew from `fromStep` to `toStep` (#53), or null when
 * there is nothing to write (same step, or a step the script does not have).
 *
 * The step change used to be written alone, so `stepStartTime` and `steps` kept describing the step
 * the crew had left: a crew moved back to step 1 saw a red `724:29:09` timer. The rules (developer,
 * 2026-10-02 and 2026-10-05):
 * - **back**: the target re-opens — `completedAt` cleared, `startedAt` kept, so the extra work counts
 *   in that step's time. **Every** later step loses its timings (meeting item 13), up to the end of
 *   the script rather than only up to where the caller thinks the crew is, so a step the crew
 *   reached after the caller read its position cannot keep timings either;
 * - **forward**: the step the crew was on is completed at the move, the target starts at the move,
 *   and the steps skipped in between get no timings.
 *
 * `order` is the script's step ids in order; `stored` the event's `steps` map. The result rides on
 * the server merge (#48): only the steps it names change. Used by the ops Update Navigator dialog.
 */
export function navigatorStepMoveTimings(
  order: string[],
  stored: Record<string, StepTiming | undefined>,
  fromStep: string | null,
  toStep: string,
  now: number,
): { stepStartTime: number; steps: Record<string, StepTiming> } | null {
  const toIdx = order.indexOf(toStep);
  if (toIdx < 0 || fromStep === toStep) { return null; }

  // Not started yet sits before the first step; a finished run sits after the last one.
  const fromIdx = fromStep === NAVIGATOR_FINISHED_STEP ? order.length : order.indexOf(fromStep ?? '');
  const steps: Record<string, StepTiming> = {};

  if (fromIdx > toIdx) {
    const reopened = { startedAt: stored[toStep]?.startedAt ?? now, completedAt: null };
    steps[toStep] = reopened;
    for (const id of order.slice(toIdx + 1)) {
      if (stored[id]) { steps[id] = CLEARED_STEP; }
    }
    return { stepStartTime: reopened.startedAt, steps };
  }

  const left = fromIdx >= 0 ? stored[order[fromIdx]] : undefined;
  if (left?.startedAt && !left.completedAt) {
    steps[order[fromIdx]] = { startedAt: left.startedAt, completedAt: now };
  }
  for (const id of order.slice(fromIdx + 1, toIdx)) {
    if (stored[id]) { steps[id] = CLEARED_STEP; }
  }
  steps[toStep] = { startedAt: now, completedAt: null };
  return { stepStartTime: now, steps };
}

export const EMAIL_PATTERN = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;
export const PHONE_PATTERN = /^\d{10}$/;

export function getRoutesToCalculate(locations: GetRoutesInput) {
  const {
    start,
    end,
    dock,
  } = locations;

  const routes: { [type: string]: DistanceLocation[] } = {
    start_end: [start, end],
    // dock_start_end_dock: [dock, start, end, dock],
    dock_start: [dock, start],
    start_dock: [start, dock],
    end_dock: [end, dock],
  };

  // Remove routes where a location is undefined or address is undefined
  for (const key in routes) {
    if (routes[key].some(location => location === undefined || location.address === undefined)) {
      delete routes[key];
    }
  }

  return routes;
};

export function convertLocationToDistanceLocation(location: Partial<Location>): DistanceLocation {
  return {
    ...location,
    address: location?.addressLineOne,
  } as DistanceLocation;
};

export function getFindQueryVariables(
  formattedDate: string,
  totalTime: number,
  eventType: string,
  zoneId?: string,
): FindQueryVariables {
  const queryVariables: FindQueryVariables = {
    timeWindow: {
      startDate: formattedDate,
      endDate: formattedDate,
      minWindowLength: totalTime,
    },
    minNumAssets: 1,
    assetsFilter: {
      types: eventTypeInfoMapV2[eventType].assetTypes,
    },
    overrideUnavailabilities: false,
  };

  // Add zoneInput only if zoneId is defined
  if (zoneId !== undefined) {
    queryVariables.zoneInput = zoneId;
  }

  return queryVariables;
}

//generate full name string from givenName and familyName fields
export function generateFullCustomerName(customer: Partial<FullUserFragment>): string {
  let fullUserName = '';

  if (customer.givenName) {
    fullUserName += customer.givenName;
  }

  if (customer.familyName) {
    if (fullUserName) {
      fullUserName += ' ';
    }
    fullUserName += customer.familyName;
  }

  return fullUserName;
}

//parse full name string to givenName and familyName
export function parseFullName(fullName?: string): { givenName?: string; familyName?: string } {
  if (!fullName) {
    return {};
  }

  const names = fullName?.trim().split(/\s+/);

  if (names.length > 1) {
    return {
      givenName: names[0],
      familyName: names.slice(1).join(' ') // Combine all parts after the first name
    };
  } else {
    return { givenName: names[0], familyName: '' };
  }
}

export function getValidationPattern(fieldName: string): string | null {
  if (fieldName === 'phone') {
    return '\\d{10}';
  } else if (fieldName === 'email') {
    return '[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}';
  }
  return null;
}

export const validateEmail = (email: string, validationErrors: { [key: string]: string }): { [key: string]: string } => {
  if (!EMAIL_PATTERN.test(email)) {
    return { ...validationErrors, email: 'Invalid email format' };
  } else {
    const { email: _email, ...rest } = validationErrors;
    return rest;
  }
};

export const validatePhone = (phone: string, validationErrors: { [key: string]: string }): { [key: string]: string } => {
  if (!PHONE_PATTERN.test(phone)) {
    return { ...validationErrors, phone: 'Invalid phone number format' };
  } else {
    const { phone: _phone, ...rest } = validationErrors;
    return rest;
  }
};

export const extractFieldsFromResponse = (fields: Partial<Field>[]): Partial<BaseFieldFragment>[] => {
  const result = fields.map(field => ({
    id: field.id,
    name: field.name,
    type: field.type,
    values: field.values?.map(value => ({
      objectId: value.objectId,
      value: value.value,
      isDefault: value.isDefault,
    }))
  }));
  return result;
};

export function hasValueV2(field: Partial<BaseFieldFragment>): boolean {
  return field?.values[0]?.value !== undefined;
}

export const populateFieldsFromResponse = (fields: Partial<BaseFieldFragment>[]): JobToolFields => {
  const fieldsResult: JobToolFields = {};

  if (!fields?.length) {
    return fieldsResult;
  }

  for (const field of fields) {
    if (hasValueV2(field)) {
      fieldsResult[field?.name] = field?.values[0].value;
    }
  }

  return fieldsResult;
}

export const getJobCustomerInfo = (job: JobDetailsV2Fragment) => {
  const customer = job?.users?.find(user => user?.role === JOB_ROLE_MAP.customerRole)?.user;
  return getCustomerInfo(customer);
}

export const getCustomerInfo = (existingCustomer?: FullUserFragment) => {

  let fullName = '';

  if (existingCustomer?.givenName) {
    fullName += existingCustomer.givenName;
  }

  if (existingCustomer?.familyName) {
    fullName += ` ${ existingCustomer.familyName }`;
  }

  return {
    selectedCustomer: existingCustomer,
    fullName,
    phone: existingCustomer?.phone || '',
    company: existingCustomer?.company || '',
    email: existingCustomer?.email || '',
    additionalContacts: existingCustomer?.additionalContacts
      ?.filter(Boolean)
      ?.map((c) => ({
        ...additionalContactInitialState,
        userId: c.userId || undefined,
        name: c.name || '',
        email: c.email || '',
        phone: c.phone || '',
        relationship: c.relationship || '',
      })) ?? [],
  }
}

export function mapAdditionalContactsToInput(contacts: AdditionalContactState[]): AdditionalContactsInput {
  const unlink = contacts
    .filter((c) => c.isUnlinked && c.userId)
    .map((c) => ({ userId: c.userId }));

  const edit = contacts
    .filter((c) => !c.isUnlinked && c.userId && c.isContactModified)
    .map((c) => ({
      userId: c.userId,
      edits: {
        name: c.name || undefined,
        email: c.email || undefined,
        phone: c.phone || undefined,
        relationship: c.relationship || undefined,
      },
    }));

  // New contacts have no userId. link is not implemented yet (TODO).
  const create = contacts
    //Contact can be created if at least one field is present
    .filter((c) => !c.isUnlinked && !c.userId && (c.name || c.email || c.phone))
    .map((c) => ({
      name: c.name || undefined,
      email: c.email || undefined,
      phone: c.phone || undefined,
      relationship: c.relationship || undefined,
    }));

  return {
    ...(create.length && { create }),
    ...(edit.length && { edit }),
    ...(unlink.length && { unlink }),
  };
}

export type CustomerProfileEditor = Pick<
  JobCreateCustomerState['customerEditor'],
  'fullName' | 'phone' | 'email' | 'company' | 'additionalContacts'
>;

export type CustomerProfileEdit = UpdateJobCustomerProfileInput;

/** Treats an unset field and an empty one as the same value, which is how the editor seeds them. */
const isSameText = (a?: string, b?: string): boolean => (a || '') === (b || '');

/**
 * Diffs the customer editor against the customer as it exists on the server, returning only the
 * fields the user actually changed -- or `undefined` when they changed nothing.
 *
 * Requirement: only fields the user actually changed are sent. The editor is seeded from the
 * customer, so sending it wholesale would rewrite every untouched field with a stale copy of
 * itself. The name is compared as the single string the user sees
 * rather than as parsed halves, because `parseFullName` does not round-trip a multi-word given
 * name ("Mary Ann Smith" parses back as "Mary" + "Ann Smith") and would report a change that
 * nobody made.
 *
 * `billingAddress` is decided by the caller: the "use end location as billing location" checkbox
 * puts an address in the editor that the user never typed, so it cannot be diffed like the rest.
 */
export function getCustomerProfileEdit(
  selectedCustomer: FullUserFragment,
  editor: CustomerProfileEditor,
  billingAddress?: LocationCreateBase,
): CustomerProfileEdit | undefined {

  const edit: CustomerProfileEdit = {};

  const currentFullName = [ selectedCustomer.givenName, selectedCustomer.familyName ].filter(Boolean).join(' ');

  if (!isSameText(editor.fullName?.trim(), currentFullName)) {

    const { givenName, familyName } = parseFullName(editor.fullName);

    // An emptied name is left alone rather than blanked: the step requires one, so this only
    // happens mid-edit.
    if (givenName) {
      edit.givenName = givenName;
      edit.familyName = familyName;
    }
  }

  if (!isSameText(editor.phone, selectedCustomer.phone)) {
    edit.phone = editor.phone;
  }

  if (!isSameText(editor.email, selectedCustomer.email)) {
    edit.email = editor.email;
  }

  if (!isSameText(editor.company, selectedCustomer.company)) {
    edit.company = editor.company;
  }

  // Already a diff: only carries contacts flagged as modified, unlinked, or newly added.
  const additionalContacts = mapAdditionalContactsToInput(editor.additionalContacts ?? []);

  if (!isEmpty(additionalContacts)) {
    edit.additionalContacts = additionalContacts;
  }

  if (billingAddress) {
    edit.billingAddress = billingAddress;
  }

  return isEmpty(edit) ? undefined : edit;
}

export const populateLocationsInputsFromResponse = (job: FullJobFragment) => {
  const locationsInputs: LocationWithHowSetIndicator = {};
  if (job?.locations?.length) {
    for (const location of job?.locations) {
      locationsInputs[ location?.locationType ] = {
        selectedLocation: location?.location,
        address: location?.location?.addressLineOne,
        unitNumber: location?.location?.addressLineTwo,
      };
    }
  }
  return locationsInputs;
}

export const populateStartTimeFromResponse = (job: JobDetailsV2Fragment) => {
  let result = [];

  if (job?.events?.length) {
    result = job?.events
      .filter((event) => event?.type === 'moving')
      .sort((event) => event?.start);
  }

  const startTime = result[0]?.start;
  return {
    startTime,
  };
}

export const populateResolvedServiceAreaFromResponse = (job: JobDetailsV2Fragment) => {
  if (job?.zone?.type === 'area') {
    return {
      name: job?.zone?.name,
      id: job?.zone?.id
    };
  } else return undefined;
}

export const populateCalculateDistancesEditInput = (job: JobDetailsV2Fragment) => {

  const result: { [key: string]: any } = {};

  job.locations.forEach(locationItem => {
    const { locationType, location } = locationItem;

    if (locationType === 'start' || locationType === 'end' || locationType === 'dock') {
      result[locationType] = location;
    }
  });

  return result;
}

// TODO: use initialJobState instead...
export const defaultJobState: Partial<JobToolState> = {
  //used to calculate final copy

  //calculated data
  selectedTimeSlot: undefined,

  //loaded data based on user inputs
  availableTimeSlots: [],
}

function cleanObject(obj: any): any {
  return transform(obj, (result: any, value, key) => {
    if (key === '__typename' ||  key === 'id' || value == null) {
      return;
    }

    if (isPlainObject(value)) {
      const cleaned = cleanObject(value);
      if (!isEmpty(cleaned)) {
        result[key] = cleaned;
      }
    } else if (isArray(value)) {
      const cleanedArray = value.map(cleanObject).filter(v => v !== undefined);
      if (cleanedArray.length > 0) {
        result[key] = cleanedArray;
      }
    } else {
      result[key] = value;
    }
  });
}

export function mergeFieldInObjects(initial: object = {}, updated: object = {}, fieldToMerge: string) {
  const cleanedInitialField = cleanObject(get(initial, fieldToMerge)) ?? {};
  const updatedField = get(updated, fieldToMerge, {});

  return {
    ...updated,
    [fieldToMerge]: {
      ...cleanedInitialField,
      ...updatedField,
    },
  };
}

export const getAddableAdditionalLocations = (locationsInputs: LocationWithHowSetIndicator) =>
    ADDABLE_LOCATIONS_V2.filter(location => !locationsInputs?.[location.key]);

export function stringifyContents(summary: Summary) {
  if (summary) {
    return {
      text: summary?.text,
      contents: JSON.stringify(summary?.contents),
    }
  }
}

export function parseContents(summary: { text: string; contents: string } | undefined) {
  if (summary && !isEmpty(summary.contents)) {
    return {
      text: summary.text,
      contents: JSON.parse(summary.contents),
    };
  }
  return undefined;
}

export const filterLocationsForAddAndRemove = (locations: { location: Partial<Location> & { remove?: boolean } }) => {
  return Object.entries(locations).reduce(
    (acc, [key, location]) => {
      if (location?.remove) {
        acc.forRemove[key] = location;
      } else {
        acc.forAdd[key] = location;
      }
      return acc;
    },
    {
      forRemove: {} as Record<string, any>,
      forAdd: {} as Record<string, any>,
    }
  );
};

export const addExistingCustomerFieldsIfDefined = (customer: User, ...fields) => {
  const fullName = `${customer.givenName} ${customer.familyName}`;

  const valuesObject = { fullName };

  fields.forEach((field) => {
    if (field.value) {
      valuesObject[field.label] = field.value;
    }
  });

  return valuesObject;
};

export type KeyExtractor<T> = (item: T) => string;

export interface FindResourcesArgs<T> {
  original: T[];
  modified: T[];
  keyExtractor: KeyExtractor<T>;
  compareFn?: (a: T, b: T) => boolean;
  detectEdited?: boolean; // Optional parameter to control detection of edited items
}

/**
 * Determines the items added, removed, and edited between two lists of items based on a key extractor function.
 * The keyExtractor function should be a function that takes an item as input and returns a string key.
 * The key should be unique for each item and should be used to compare the lists.
 * The key should contain items to compare separated by '-'.
 *
 * @param original - The original list of items.
 * @param modified - The modified list of items.
 * @param keyExtractor - A function to extract a unique key from each item to compare the lists.
 * @param compareFn - A function to compare two items and determine if they are equal.
 * @param detectEdited - Optional parameter to control detection of edited items. Default is false.
 * @returns An object containing three arrays: `added`, `removed`, and `edited`.
 *
 * @example
 * // Example keyExtractor function for comparing based on user id and role
 * const keyExtractor = (item: AttendeeWithName) => `${item.user.id}-${item.role}`;
 * const compareFn = (a: AttendeeWithName, b: AttendeeWithName) => a.someProperty === b.someProperty;
 * const { added, removed, edited } = getResourceChanges({ original, modified, keyExtractor, compareFn });
 */
export function getResourceChanges<T>({
  original,
  modified,
  keyExtractor,
  compareFn = () => false, // Default compare function always returns false
  detectEdited = false // Default value is false
}: FindResourcesArgs<T>) {
  // Create maps for quick lookup
  const originalMap = new Map<string, T>();
  const modifiedMap = new Map<string, T>();

  // Populate the maps
  original.forEach((item) => originalMap.set(keyExtractor(item), item));
  modified.forEach((item) => modifiedMap.set(keyExtractor(item), item));

  // Find removed items (items present in originalMap but not in modifiedMap)
  const removed = original.filter((item) => !modifiedMap.has(keyExtractor(item)));

  // Find added items (items present in modifiedMap but not in originalMap)
  const added = modified.filter((item) => !originalMap.has(keyExtractor(item)));

  // Find edited items (items present in both maps but with different values)
  let edited: T[] = [];
  if (detectEdited) {
    edited = modified.filter(
      (item) => originalMap.has(keyExtractor(item)) && !compareFn(item, originalMap.get(keyExtractor(item))!)
    );
  }

  return { added, removed, edited };
}

// export function checkAndUpdateEmptyRoomName(inventory: JobInventory): JobInventory {
//   return {
//     ...inventory,
//     rooms: inventory.rooms.map(room => {
//       if (room.name === '') {
//         return { ...room, name: 'Inventory List Room' };
//       }
//       return room;
//     })
//   };
// }

export const calculatePercentageChargesSubtotals = (eventId: string, updatedCharges: Charge[]) => {
  let fixedTotal = 0;

  const eventCharges = updatedCharges.filter(
      charge => charge.calendarEvent?.id === eventId
  );

  const clonedCharges = cloneDeep(eventCharges);
  for (const charge of clonedCharges) {
      const priceType = charge?.price?.priceType;
      if (priceType === 'fixed') {
          fixedTotal += charge.chargeSubTotal || 0;
      }
  }

  const percentBasedCharges = clonedCharges.filter(charge => {
      const priceType = charge?.price?.priceType;
      return priceType === 'percentage';
  });

  for (const charge of percentBasedCharges) {
      const currentSubtotal = charge.chargeSubTotal || 0;
      const newSubtotal = fixedTotal * (charge.quantity / 100 * charge.amount);

      if (currentSubtotal !== newSubtotal) {
          charge.chargeSubTotal = newSubtotal;
      }
  }

  return clonedCharges;
};

export function unifyNumberFormat(phone: string): string {
  return phone.replace(/\D/g, '');
}

export function formatDateTimeWithTimezone(timestamp: number, timezone: string) {
  const dayJsObject = dayjs(timestamp * 1000).tz(timezone);
  if (!dayJsObject.isValid()) {
    return { date: 'Invalid Date', time: 'Invalid Time' };
  }

  return {
    date: dayJsObject.format('MMM DD YYYY'),
    time: dayJsObject.get('minute') === 0 ? dayJsObject.format('hA') : dayJsObject.format('h:mmA'),
  };
}

// Requirement: handle timezone in selector to avoid issue with time when opening job from
// the screen pop and switching between zones
export function formatEventDate(start: number | null | undefined, timezone: string | undefined): string {
  if (!start) { return 'Not Booked'; }
  return dayjs(start * 1000).tz(timezone).format('h:mm A, MMM D');
}

export function isEventMissingLocations(
  eventsWithMissingLocations: Record<string, string[]> | undefined,
  eventId: string | undefined
): boolean {
  if (!eventId || !eventsWithMissingLocations) {
    return false;
  }
  return Object.keys(eventsWithMissingLocations).includes(eventId);
}

export function getMissingLocations(
  eventsWithMissingLocations: Record<string, string[]> | undefined,
  eventId: string | undefined
): string[] {
  if (!eventId || !eventsWithMissingLocations) {
    return [];
  }
  return eventsWithMissingLocations[eventId] || [];
}

export function getDamagesFromCharges(charges: ChargeWithLockedAndInvoicedFlag[]){
  return charges?.filter(c =>
    Array.isArray(c?.attributes) && c.attributes.includes('damage'));
}

export function getFilteredLocationInfo(existingLocationInfo: LocationBase) {
  return {
      addressLineOne: existingLocationInfo?.addressLineOne,
      areaCode: existingLocationInfo?.areaCode,
      city: existingLocationInfo?.city,
      country: existingLocationInfo?.country,
      countryJurisdiction: existingLocationInfo?.countryJurisdiction,
      name: isNil(existingLocationInfo?.name) ? undefined : existingLocationInfo?.name,
      coordinates: existingLocationInfo?.coordinates
          ? {
                latitude: existingLocationInfo.coordinates.latitude,
                longitude: existingLocationInfo.coordinates.longitude,
            }
          : undefined,
  };
}

export function mapEventsToSuggestions(events, lockDate) {
  return events?.map(e => ({
    id: e.id,
    label: e.title,
    disabled: !!(e.invoices?.some(isFinalizedInvoice) || (e?.end && lockDate > e?.end)),
    invoiceStatus: getEventInvoicedStatus(e.invoices),
    event: {
      id: e.id,
      title: e.title,
      status: e.status,
      start: e.start,
    }
  }));
}

export function getGrossProfitMargin<K extends { discountedSubTotal?: number; expenseTotal?: number }>(data: K): number {
  if (!data || !data.discountedSubTotal || !data.expenseTotal) {
    return 0;
  }
  return ((data.discountedSubTotal - data.expenseTotal) / data.discountedSubTotal) * 100;
}

export function getRequiredLocationsTypes(
  eventType: string,
  eventInfoMap: { [key: string]: EventTypeInfo; }) {
  return (eventType && eventInfoMap[eventType]?.requiredLocations) ?? ['start', 'end'];
}

export interface LocationValidationResult {
  missingLocations: string[];   // Location types completely missing from job
  invalidLocations: string[];   // Location types present but missing addressLineOne
  isValid: boolean;
  errorMessage: string | null;
}

export function validateLocationsForEventType(
  jobLocations: Job_LocationsFragment['locations'],
  eventType: string
): LocationValidationResult {
  const requiredLocations = getRequiredLocationsTypes(eventType, eventTypeInfoMapV2);
  const missingLocations: string[] = [];
  const invalidLocations: string[] = [];

  for (const locationType of requiredLocations) {
    const jobLocation = jobLocations?.find(l => l.locationType === locationType);
    if (!jobLocation) {
      missingLocations.push(locationType);
    } else if (!jobLocation.location?.addressLineOne?.trim()) {
      invalidLocations.push(locationType);
    }
  }

  const isValid = missingLocations.length === 0 && invalidLocations.length === 0;
  let errorMessage: string | null = null;
  if (!isValid) {
    const parts: string[] = [];
    if (missingLocations.length > 0) parts.push(`Missing: ${missingLocations.join(', ')}`);
    if (invalidLocations.length > 0) parts.push(`Invalid (no address): ${invalidLocations.join(', ')}`);
    errorMessage = parts.join('. ');
  }

  return { missingLocations, invalidLocations, isValid, errorMessage };
}

export function getDefaultLocationsForEventType(
  jobLocations: Job_LocationsFragment['locations'],
  eventType: string): CalendarEventLocationInput[] {

  const requiredLocations = getRequiredLocationsTypes(eventType, eventTypeInfoMapV2);

  return jobLocations
    .filter(location => requiredLocations.includes(location.locationType))
    .map(location => ({
      locationId: location.locationId,
      type: location.locationType,
      order: requiredLocations.indexOf(location.locationType),
      estimatedTimeAtLocation: 0,
      ignoreInDuration: true,
    }));
}

/**
 * The event fields navigator URL resolution reads. Structural so both the workorders
 * editor rows and a raw calendar event fragment satisfy it.
 */
export interface NavigatorUrlEvent {
  type?: string;
  navigatorUrl?: string | null;
}

export interface NavigatorUrlConfig {
  /** `job.metadata.navigatorUrl` — per-job override */
  jobNavigatorUrl?: string | null;
  /** `navigator.url-<eventType>` keyed by event type — `selectNavigatorUrlsByEventType` */
  typeUrls?: { [eventType: string]: string | undefined };
  /** `calendarEvents.types` — or the code defaults when the zone has no such config */
  eventTypes?: { [key: string]: EventTypeInfo };
  /** `navigator.url` */
  defaultUrl?: string | null;
}

/**
 * The single navigator URL resolution chain.
 *
 * Button visibility and the launch itself must both go through this. While they were two
 * separate inline chains they disagreed: the button chain never read
 * `calendarEvents.types` (so a navigator configured there had no button to launch it) and
 * the launch chain never read `event.navigatorUrl`, so a button computed from one script
 * could launch another.
 *
 * Strictly most-specific to least-specific:
 *
 * 1. `navigator.url-<eventType>` — one config key per event type defined in code. A plain
 *    string, editable as an ordinary text field in Settings. The normal way to configure.
 * 2. `calendarEvents.types[<type>].navigatorUrl` — the escape hatch for event types that
 *    exist only in zone config (`book-off`, `otr-fees`, `storage`, `generic`) and so
 *    cannot have a compile-time config key of their own.
 * 3. `navigator.url` — the zone default, applying to any event type with nothing above.
 *
 * Tier 3 is what caused issue #10's over-showing: with the per-type keys unwired it was the
 * only switch that worked, so turning the feature on at all put a 17-step moving workflow on
 * every booked event. The per-type keys are real now, so a zone that wants one event type
 * says so and leaves this unset — the tier is a deliberate "same script everywhere" choice
 * rather than the only option.
 *
 * Note a script cannot yet adapt to the event it is running on: `getSteps(input, state)`
 * is not passed the event type, and the `eventType` prop a script declares on
 * `<EventNavigator>` is consumed by nothing. Until that is wired up (issue #13), tier 3 only
 * makes sense for a script that is type-agnostic by construction.
 */
export function resolveNavigatorUrl(
  event: NavigatorUrlEvent | undefined,
  config: NavigatorUrlConfig,
): string | null {

  if (!event) { return null; }

  // Pinned on the event when the navigator is first launched, so a config change
  // mid-move cannot swap the script out from under a crew.
  if (event.navigatorUrl) { return event.navigatorUrl; }

  if (config.jobNavigatorUrl) { return config.jobNavigatorUrl; }

  if (event.type) {
    const typeKeyUrl = config.typeUrls?.[event.type];
    if (typeKeyUrl) { return typeKeyUrl; }

    const eventTypeConfigUrl = config.eventTypes?.[event.type]?.navigatorUrl;
    if (eventTypeConfigUrl) { return eventTypeConfigUrl; }
  }

  return config.defaultUrl || null;
}

/**
 * Whether a navigator session is genuinely under way, as opposed to an event merely carrying
 * `navigatorCurrentStep === 'started'` from a first write or a reset. Drives the
 * Start/Continue label on both navigator buttons.
 *
 * `'started'` is a sentinel written before the crew confirms anything, so it alone does not
 * mean a session is in progress — only a persisted `stepStartTime` does. Any other non-null
 * step is a real step id and therefore is in progress.
 */
export function isNavigatorStarted(event: {
  navigatorCurrentStep?: string | null;
  navigatorData?: string | null;
}): boolean {

  const step = event?.navigatorCurrentStep;
  if (!step) { return false; }

  if (step !== 'started') { return true; }

  try {
    const navData = event.navigatorData ? JSON.parse(event.navigatorData) : {};
    return !!navData.stepStartTime;
  } catch {
    return false;
  }
}

/**
 * Requirement: an appointment that nonetheless carries charges does move reconciled revenue when
 * it is cancelled, so it keeps the lock despite its type being exempt.
 */
export function isLockExemptNonRevenueEvent(event?: { type?: string; total?: number | null }): boolean {
  if (!event?.type || !LOCK_EXEMPT_NON_REVENUE_EVENT_TYPES.includes(event.type)) { return false; }

  return !event.total;
}

export function getPromoteEventInfo(event: { status: JobDetailsV2Fragment['events'][number]['status']}) {
  return {
    label: event.status === 'booked' ? 'Mark Confirmed' : 'Mark Completed',
    icon: 'pi pi-angle-double-up',
    visible: [ 'booked', 'confirmed' ].includes(event.status),
  };
}

/**
 * The step a navigator run should resume on, derived from a single CalendarEvent.
 *
 * `'started'` is a launch sentinel, not a step: `startEvent` writes it once when the navigator is
 * opened, before the crew has confirmed anything. Treating it as a step id would put them on a
 * step that does not exist in the script, so it resolves to "no confirmed step" and falls through
 * to the step-timing map.
 *
 * Used by both the navigator page's resume path and its tree build, which derived this
 * independently and had already drifted apart once. Keep it the only copy.
 *
 * Deliberately pure and event-scoped: this is called at mount, not on every job change. #46 was
 * caused by re-deriving the crew's active step from shared job state continuously — see
 * `docs/crew-navigator.md`. A caller that finds itself invoking this in a subscription over
 * `selectJob` has reintroduced the bug.
 */
export function deriveNavigatorResume(event: {
  navigatorCurrentStep?: string | null;
  navigatorData?: string | null;
} | null | undefined): { stepId: string | null; stepStartTime: number | null } {

  const rawStep = event?.navigatorCurrentStep;

  let parsed: { stepStartTime?: number; steps?: Record<string, StepTiming> } | null = null;
  try {
    parsed = event?.navigatorData ? JSON.parse(event.navigatorData) : null;
  } catch { /* malformed navigatorData must not break the resume */ }

  // A real confirmed step wins outright.
  let stepId: string | null = (rawStep && rawStep !== NAVIGATOR_STARTED_SENTINEL) ? rawStep : null;

  // Otherwise resume the most recently started step that was never completed. This is what makes
  // a reload mid-run land in the right place when only the sentinel was ever persisted.
  if (!stepId && parsed?.steps) {
    const inProgress = Object.entries(parsed.steps)
      .filter(([ , timing ]) => timing?.startedAt != null && timing?.completedAt == null)
      .sort(([ , a ], [ , b ]) => (b.startedAt ?? 0) - (a.startedAt ?? 0));

    if (inProgress.length > 0) { stepId = inProgress[0][0]; }
  }

  // Prefer the resolved step's OWN recorded start over the global `stepStartTime`.
  //
  // `stepStartTime` is a single value for the whole run and is only written by the crew's own
  // advance. Anything that moves `navigatorCurrentStep` without also writing `navigatorData` leaves
  // the two describing different steps — the ops dialog's step dropdown does exactly that
  // (`update-navigator-dialog.component.ts`, `onCurrentStepChange`), and a stale-snapshot clobber
  // (#48) achieves the same by rolling `navigatorData` back under a newer `currentStep`.
  //
  // Measured on event `f8a8ebb4` 2026-08-18: `currentStep` was `walkthrough_scope` while
  // `stepStartTime` still held `arrival_start`'s start from 4h 45m earlier, so the crew's step timer
  // read `4:45:00` instead of `08:00`. Reading the step's own `startedAt` makes the timer consistent
  // with the step it is labelling, whatever else disagrees.
  // When a step is resolved, its own `startedAt` is the only trustworthy start for it. If it has
  // none, return `null` and let the caller stamp "now" — do **not** fall back to `stepStartTime`,
  // which belongs to a different step and can be hours out.
  //
  // A step reached by a normal advance always has an entry (`syncNavigatorStep` writes
  // `steps[stepId].startedAt`), so "resolved but no entry" is by definition the anomalous case: ops
  // jumping the crew forward to a step never started, or a clobber that lost the entry. On
  // `f8a8ebb4` the JSON map had lost `walkthrough_scope` while the flattened mirror still held it,
  // and inheriting `stepStartTime` there showed `4:52:34` on a step the crew reached 8 minutes
  // earlier. Starting the clock at mount is imperfect but bounded; inheriting is unbounded.
  //
  // `stepStartTime` is still honoured when no step is resolved at all — that is the fresh-start
  // case, where it is the only record that the first step's timer has already been running.
  if (stepId) {
    return { stepId, stepStartTime: parsed?.steps?.[stepId]?.startedAt ?? null };
  }

  return { stepId, stepStartTime: parsed?.stepStartTime ?? null };
}
